/**
 * Resolving studio plugins (SAL-2912).
 *
 * Enabled state lives in `Workspace.plugins` — a `{ [key]: boolean }` map — for
 * one reason above all: NextAuth already puts the whole workspace row on
 * `session.workspace` (see the session callback in app/api/auth/[...nextauth]),
 * so every client component that renders navigation already holds it. A
 * WorkspaceModule row would have needed another round trip, which is exactly
 * how NavMenu ended up with three separate `fetchXEnabled()` effects.
 *
 * An absent key means "no one has decided", so the registry's `defaultEnabled`
 * applies. That is what makes a new plugin shippable without a migration.
 *
 * Everything here is pure and client-safe. Server-only helpers (route guards,
 * usage counts) live in ./guard.js and ./usage.js.
 */

import {
  PLUGINS,
  getPlugin,
  getPluginOwningHref,
  getPluginOwningSection,
} from "@/lib/plugins/registry";

export {
  PLUGINS,
  PLUGIN_KEYS,
  getPlugin,
  getPluginOwningHref,
  getPluginOwningSection,
} from "@/lib/plugins/registry";

function readSettings(workspace) {
  const settings = workspace?.plugins;
  return settings && typeof settings === "object" && !Array.isArray(settings)
    ? settings
    : null;
}

/**
 * Is `key` on for this studio?
 *
 * @param {object|null} workspace  A Workspace row (e.g. `session.workspace`).
 *   Only `plugins` is read.
 * @param {string} key
 */
export function isPluginEnabled(workspace, key) {
  const descriptor = getPlugin(key);
  // An unknown key is not a plugin, so it is not something to hide. Returning
  // true here keeps a typo from silently blanking a menu.
  if (!descriptor) return true;

  const stored = readSettings(workspace)?.[key];
  return typeof stored === "boolean" ? stored : descriptor.defaultEnabled;
}

/**
 * The studio's full plugin state, including defaults for keys it has never set.
 * @returns {Record<string, boolean>}
 */
export function resolvePluginSettings(workspace) {
  const stored = readSettings(workspace);
  return Object.fromEntries(
    PLUGINS.map((plugin) => [
      plugin.key,
      typeof stored?.[plugin.key] === "boolean"
        ? stored[plugin.key]
        : plugin.defaultEnabled,
    ]),
  );
}

/** Keys that are on, in registry order. */
export function getEnabledPluginKeys(workspace) {
  const settings = resolvePluginSettings(workspace);
  return PLUGINS.filter((plugin) => settings[plugin.key]).map(
    (plugin) => plugin.key,
  );
}

/**
 * Should a link to `href` be shown to this studio?
 *
 * Hrefs no plugin owns are always visible, so this is safe to apply to a whole
 * menu without annotating individual items.
 */
export function isHrefVisible(workspace, href) {
  const owner = getPluginOwningHref(href);
  return owner === null || isPluginEnabled(workspace, owner);
}

/**
 * Drop nav items belonging to disabled plugins, at any depth.
 *
 * This is the one enforcement point every menu shares. It works on the item
 * shape used across the app — `{ href, label|name, subItems }` — and follows
 * the same rule as lib/workspaces/menu-override.js: a group with no surviving
 * children is dropped too, so a section header never renders above an empty
 * list.
 *
 * Items are matched by href alone. Nothing has to be tagged with a plugin key,
 * which is what stops the six nav definitions from drifting apart the next time
 * one of them gains a link.
 */
export function filterNavItemsByPlugins(items, workspace) {
  if (!Array.isArray(items)) return items;

  return items.reduce((kept, item) => {
    if (!item) return kept;

    const children = Array.isArray(item.subItems)
      ? filterNavItemsByPlugins(item.subItems, workspace)
      : null;

    // A group (no href, or the placeholder "#") survives on its children.
    const hasHref = typeof item.href === "string" && item.href !== "#";
    if (!hasHref) {
      if (children && children.length === 0) return kept;
      kept.push(children ? { ...item, subItems: children } : item);
      return kept;
    }

    if (!isHrefVisible(workspace, item.href)) return kept;

    kept.push(children ? { ...item, subItems: children } : item);
    return kept;
  }, []);
}

/**
 * Normalize a settings patch from the admin form: keep only known keys, coerce
 * to booleans, and drop anything the registry doesn't recognise so a stale
 * client can't write junk into the column.
 */
export function sanitizePluginSettings(input) {
  if (!input || typeof input !== "object" || Array.isArray(input)) return {};
  return Object.fromEntries(
    PLUGINS.filter((plugin) => plugin.key in input).map((plugin) => [
      plugin.key,
      Boolean(input[plugin.key]),
    ]),
  );
}
